import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Send, X } from "lucide-react";

const varsIn = (text: string) => [...new Set(Array.from(text.matchAll(/\{\{\s*(\d+)\s*\}\}/g), (m) => m[1]!))];

/** Create a template inside the CRM and submit it to Meta for approval */
export function TemplateCreator({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; onCreated: () => void }) {
  const { success, error } = useToast();
  const [form, setForm] = useState({ name: "", language: "en", category: "UTILITY", header_text: "", body_text: "", footer_text: "" });
  const [examples, setExamples] = useState<string[]>([]);
  const [buttons, setButtons] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ name: "", language: "en", category: "UTILITY", header_text: "", body_text: "", footer_text: "" });
    setExamples([]);
    setButtons([]);
  }, [open]);

  const bodyVars = useMemo(() => varsIn(form.body_text), [form.body_text]);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/api/WhatsApp/Templates", {
        ...form,
        name: form.name.trim(),
        header_text: form.header_text.trim() || undefined,
        footer_text: form.footer_text.trim() || undefined,
        body_examples: bodyVars.map((_, i) => examples[i] || ""),
        buttons: buttons.map((b) => b.trim()).filter(Boolean),
      });
      success("Template submitted", "Meta usually reviews it within minutes to a few hours. Sync templates to see the status.");
      onOpenChange(false);
      onCreated();
    } catch (err: any) {
      error("Not submitted", err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New WhatsApp template</DialogTitle>
          <DialogDescription>
            Submitted to Meta for approval. For a template with a PDF/image header (e.g. quotation), create it in Meta's WhatsApp Manager instead.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1 sm:col-span-1">
            <Label className="text-xs">Name</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))} placeholder="quotation_follow_up" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Language</Label>
            <Select value={form.language} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[
                  ["en", "English"],
                  ["en_US", "English (US)"],
                  ["hi", "Hindi"],
                  ["ta", "Tamil"],
                  ["gu", "Gujarati"],
                  ["mr", "Marathi"],
                ].map(([v, l]) => (
                  <SelectItem key={v} value={v!}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UTILITY">Utility (orders, quotes, updates)</SelectItem>
                <SelectItem value="MARKETING">Marketing (new designs, season collections, offers)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Header (optional, max 60)</Label>
          <Input value={form.header_text} maxLength={60} onChange={set("header_text")} placeholder="Quotation update" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Body — use {"{{1}}"}, {"{{2}}"}… for values filled in when sending</Label>
          <Textarea rows={5} value={form.body_text} maxLength={1024} onChange={set("body_text")} placeholder={"Hello {{1}}, our quotation {{2}} for {{3}} is ready. Please check the rates, quantity tolerance and delivery date and reply here if you need a sample or any change."} />
        </div>
        {bodyVars.length > 0 && (
          <div className="rounded-lg bg-muted/40 p-3 space-y-2">
            <p className="text-xs text-muted-foreground">Example values (Meta requires them to approve the template)</p>
            {bodyVars.map((v, i) => (
              <div key={v} className="flex items-center gap-2">
                <span className="w-12 text-xs font-mono">{`{{${v}}}`}</span>
                <Input className="h-8 text-xs" value={examples[i] || ""} onChange={(e) => setExamples((list) => bodyVars.map((_, idx) => (idx === i ? e.target.value : list[idx] || "")))} />
              </div>
            ))}
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Footer (optional)</Label>
          <Input value={form.footer_text} maxLength={60} onChange={set("footer_text")} placeholder="Nelgates Textiles" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Quick-reply buttons (optional, up to 3)</Label>
          {buttons.map((b, i) => (
            <div key={i} className="flex gap-2">
              <Input value={b} maxLength={25} onChange={(e) => setButtons((list) => list.map((x, idx) => (idx === i ? e.target.value : x)))} />
              <Button type="button" variant="ghost" size="sm" className="h-9 w-9 p-0" aria-label="Remove button" onClick={() => setButtons((list) => list.filter((_, idx) => idx !== i))}>
                <X className="size-4" />
              </Button>
            </div>
          ))}
          {buttons.length < 3 && (
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => setButtons((l) => [...l, ""])}>
              <Plus className="size-3.5" /> Add button
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !form.name || !form.body_text.trim()} className="gap-1.5">
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            Submit to Meta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
